import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/ui/atoms/Button';
import Eyebrow from '@/ui/atoms/Eyebrow';
import { useFireOncePerUser } from '@/hooks/useFireOncePerUser';
import { useUnlockMilestone, type MilestoneId } from '@/store/unlockMilestone';

// UnlockMilestoneBanner — P4 milestone unlock banner.
// FSM: state_machines/unlock_milestone.yaml.
// 1 component instance per milestone_id; parent decides which to mount based
// on me.councils_run + already-fired state.

const MILESTONE_COPY: Record<MilestoneId, { i18n: string; tone: 'jade' | 'gold' | 'azure' | 'vermilion' }> = {
  first_council: { i18n: 'ahaDash.unlock.first', tone: 'jade' },
  ten_councils: { i18n: 'ahaDash.unlock.ten', tone: 'azure' },
  fifty_councils: { i18n: 'ahaDash.unlock.fifty', tone: 'gold' },
  hundred_councils: { i18n: 'ahaDash.unlock.hundred', tone: 'vermilion' },
};

interface UnlockMilestoneBannerProps {
  milestoneId: MilestoneId;
  councilsRun: number;
  onDismiss?: () => void;
}

export function UnlockMilestoneBanner({
  milestoneId,
  councilsRun,
  onDismiss,
}: UnlockMilestoneBannerProps) {
  const { t } = useTranslation();
  const dismiss = useUnlockMilestone((s) => s.dismiss);
  const dismissedAt = useUnlockMilestone((s) => s.records[milestoneId].dismissedAt);
  const copy = MILESTONE_COPY[milestoneId];

  const eventName = `unlock_milestone_${milestoneId}_fired`;
  const storageKey = `fyd_unlock_${milestoneId}`;
  const { fired, fire } = useFireOncePerUser(eventName, storageKey);

  useEffect(() => {
    if (!fired) {
      fire({
        user_id: 'auth',
        councils_run: councilsRun,
        timestamp: new Date().toISOString(),
      });
    }
  }, [fired, fire, councilsRun]);

  if (dismissedAt) return null;

  const onDismissClick = () => {
    dismiss(milestoneId);
    onDismiss?.();
  };

  return (
    <div
      className="aha-unlock-reveal px-6 py-4 border border-hairline bg-paper rounded-md flex items-start justify-between gap-4 mb-6"
      role="status"
      data-testid={`unlock-banner-${milestoneId}`}
    >
      <div className="flex-1">
        <Eyebrow tone={copy.tone} className="mb-2">
          {t(`ahaDash.unlock.${milestoneId === 'first_council' ? 'first' : milestoneId === 'ten_councils' ? 'ten' : milestoneId === 'fifty_councils' ? 'fifty' : 'hundred'}`).split('·')[0].trim()}
        </Eyebrow>
        <p className="font-serif text-lg text-ink leading-relaxed">{t(copy.i18n)}</p>
      </div>
      <Button variant="ghost" onClick={onDismissClick} size="sm">
        {t('ahaDash.unlock.dismiss')}
      </Button>
    </div>
  );
}

export default UnlockMilestoneBanner;
